import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Camera, 
  Edit2, 
  Shield, 
  Settings,
  Eye,
  Heart,
  Star,
  Users,
  MessageCircle,
  Loader2,
  CheckCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import PhotoGallery from '@/components/vivah/PhotoGallery';
import VerificationPanel from '@/components/vivah/VerificationPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/vivah`;

export default function VivahMyProfilePage({ user }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('photos');

  useEffect(() => {
    if (user) {
      fetchProfile();
      fetchStats();
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/profile/me?token=${token}`);
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/stats?token=${token}`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return '';
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const heightToString = (cm) => {
    if (!cm) return '';
    const totalInches = cm / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    return `${feet}'${inches}"`;
  };

  const getVerificationBadge = () => {
    const level = profile?.verification_level || 0;
    if (level >= 2) return { label: 'Gold Verified', color: 'bg-gradient-to-r from-amber-500 to-yellow-500' };
    if (level >= 1) return { label: 'Phone Verified', color: 'bg-gradient-to-r from-green-500 to-emerald-500' };
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-rose-600 animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50 flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-800 mb-4">No Profile Found</h2>
          <button
            onClick={() => navigate('/vivah/register')}
            className="px-6 py-3 bg-gradient-to-r from-rose-600 to-amber-600 text-white font-medium rounded-xl"
          >
            Create Your Profile
          </button>
        </div>
      </div>
    );
  }

  const badge = getVerificationBadge();
  const basic = profile.basic || {};
  const contact = profile.contact || {};
  const eduCareer = profile.education_career || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-amber-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-rose-600 to-amber-600 text-white">
        <div className="px-4 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate('/vivah')}
            className="p-2 hover:bg-white/10 rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold flex-1">My Profile</h1>
          <button 
            onClick={() => navigate('/vivah/register')}
            className="p-2 hover:bg-white/10 rounded-full"
            title="Edit Profile"
          >
            <Edit2 className="w-5 h-5" />
          </button>
        </div>

        {/* Profile Header */}
        <div className="px-4 pb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              {profile.profile_photo ? (
                <img 
                  src={profile.profile_photo} 
                  alt={basic.full_name}
                  className="w-20 h-20 rounded-full object-cover border-4 border-white/30"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center border-4 border-white/30">
                  <Camera className="w-8 h-8 text-white/70" />
                </div>
              )}
              {badge && (
                <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-amber-500 rounded-full flex items-center justify-center border-2 border-white">
                  <Shield className="w-4 h-4 text-white" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold">{basic.full_name}</h2>
              <p className="text-rose-200">{profile.profile_id}</p>
              {badge && (
                <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium mt-1 ${badge.color}`}>
                  <Shield className="w-3 h-3" />
                  {badge.label}
                </div>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-2 mt-4">
            <div className="bg-white/10 rounded-xl p-2 text-center">
              <Eye className="w-4 h-4 mx-auto mb-1" />
              <p className="text-lg font-bold">{stats?.profile_views || 0}</p>
              <p className="text-xs text-rose-200">Views</p>
            </div>
            <div className="bg-white/10 rounded-xl p-2 text-center">
              <Heart className="w-4 h-4 mx-auto mb-1" />
              <p className="text-lg font-bold">{stats?.interests_received || 0}</p>
              <p className="text-xs text-rose-200">Interests</p>
            </div>
            <div className="bg-white/10 rounded-xl p-2 text-center">
              <Users className="w-4 h-4 mx-auto mb-1" />
              <p className="text-lg font-bold">{stats?.accepted_matches || 0}</p>
              <p className="text-xs text-rose-200">Matches</p>
            </div>
            <div className="bg-white/10 rounded-xl p-2 text-center">
              <Star className="w-4 h-4 mx-auto mb-1" />
              <p className="text-lg font-bold">{stats?.shortlisted_by || 0}</p>
              <p className="text-xs text-rose-200">Saved</p>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Completion */}
      {(profile.profile_completion || 0) < 100 && (
        <div className="mx-4 -mt-4 mb-4 bg-white rounded-xl p-4 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Profile Completion</span>
            <span className="text-sm font-bold text-rose-600">{profile.profile_completion || 0}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-rose-500 to-amber-500 h-2 rounded-full transition-all"
              style={{ width: `${profile.profile_completion || 0}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Complete your profile to get more matches!
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 bg-white sticky top-0 z-10">
        {['photos', 'verification', 'details'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-3 text-sm font-medium capitalize transition-colors ${
              activeTab === tab 
                ? 'text-rose-600 border-b-2 border-rose-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-4">
        {activeTab === 'photos' && (
          <PhotoGallery 
            photos={profile.photos || []} 
            profilePhoto={profile.profile_photo}
            onUpdate={fetchProfile}
          />
        )}

        {activeTab === 'verification' && (
          <VerificationPanel onUpdate={fetchProfile} />
        )}

        {activeTab === 'details' && (
          <div className="space-y-4">
            {/* Basic Info */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="font-bold text-gray-800 mb-3">Basic Information</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">Age</span>
                  <p className="font-medium">{calculateAge(basic.dob)} years</p>
                </div>
                <div>
                  <span className="text-gray-500">Height</span>
                  <p className="font-medium">{heightToString(basic.height_cm)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Marital Status</span>
                  <p className="font-medium">{basic.marital_status}</p>
                </div>
                <div>
                  <span className="text-gray-500">Location</span>
                  <p className="font-medium">{contact.city}, {contact.state}</p>
                </div>
              </div>
            </div>

            {/* Education & Career */}
            <div className="bg-white rounded-2xl p-4 shadow-sm">
              <h3 className="font-bold text-gray-800 mb-3">Education & Career</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-500">Education</span>
                  <p className="font-medium">{eduCareer.highest_education}</p>
                </div>
                <div>
                  <span className="text-gray-500">Profession</span>
                  <p className="font-medium">{eduCareer.profession}</p>
                </div>
                <div>
                  <span className="text-gray-500">Income</span>
                  <p className="font-medium">{eduCareer.annual_income}</p>
                </div>
              </div>
            </div>

            {/* About Me */}
            {profile.about_me && (
              <div className="bg-white rounded-2xl p-4 shadow-sm">
                <h3 className="font-bold text-gray-800 mb-3">About Me</h3>
                <p className="text-sm text-gray-600">{profile.about_me}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 safe-area-bottom">
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/vivah/chat')}
            className="flex-1 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl flex items-center justify-center gap-2 hover:bg-gray-200 transition-colors"
          >
            <MessageCircle className="w-5 h-5" />
            Messages
          </button>
          <button
            onClick={() => navigate('/vivah/interests')}
            className="flex-1 py-3 bg-gradient-to-r from-rose-500 to-rose-600 text-white font-medium rounded-xl flex items-center justify-center gap-2 hover:shadow-lg transition-shadow"
          >
            <Heart className="w-5 h-5" />
            View Interests
            {(stats?.pending_interests || 0) > 0 && (
              <span className="bg-white text-rose-600 text-xs px-2 py-0.5 rounded-full">
                {stats.pending_interests}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
